


import {
  computed,
  defineComponent,
  PropType,
  reactive,
  toRef,
} from '@vue/composition-api';
import type {
  HokkaidoAirportReportCar,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type {
  HokkaidoAirportLabelSp,
  HokkaidoAirportSettings,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import type {
  HokkaidoAirportReportPostWorkInspectionParams,
  HokkaidoAirportReportPostWorkInspectionSkipParams,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportReportRequest';
import type { InspectionInputState } from '@/components/Sp/ExternalReports/HokkaidoAirport/types';
import hokkaidoAirportReportApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_report';
import { useCarTypeChoices } from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useCarTypeChoices';
import {
  useInspectionInput,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useInspectionInput';
import {
  useInspectionSubmission,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/composables/useInspectionSubmission';
import { isVehicleSelectionLocked } from '@/lib/externalReports/hokkaidoAirportInspection';
import InspectionForm from '@/components/Sp/ExternalReports/HokkaidoAirport/InspectionForm/index.vue';
import VehicleSelectFields from '@/components/Sp/ExternalReports/HokkaidoAirport/VehicleSelectFields/index.vue';

export default defineComponent({
  components: { InspectionForm, VehicleSelectFields },
  props: {
    hokkaidoAirportCar: { type: Object as PropType<HokkaidoAirportReportCar>, required: true },
    userSettings: { type: Object as PropType<HokkaidoAirportSettings>, required: true },
  },
  emits: ['finishPostWorkInspection', 'showErrorOnCommon'],
  setup(props, { emit }) {
    const { isRequesting, confirmModalState, submitInspection } = useInspectionSubmission((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });
    const spLabels = computed<HokkaidoAirportLabelSp>(() => props.userSettings.sp);
    const airports = computed<string[]>(() => props.userSettings.choices.work_routes);
    const state = reactive<InspectionInputState>({
      selectedAirport: props.hokkaidoAirportCar.report.airportName,
      selectedCarType: props.hokkaidoAirportCar.report.carType,
      // 初期値は useInspectionInput の即時実行の watch が、報告書の空港名・車種の点検項目から作る
      inspectionResults: {},
    });
    const isSelectionLocked = computed<boolean>(() => isVehicleSelectionLocked(props.hokkaidoAirportCar.report));
    const { carTypes } = useCarTypeChoices(toRef(props, 'userSettings'), state);
    const { hasVehicleSelection, isAllInspected } = useInspectionInput(toRef(props, 'userSettings'), state);
    const canComplete = computed<boolean>(() => {
      return !isRequesting.value && hasVehicleSelection.value && isAllInspected.value;
    });

    // 開始前点検をスキップした報告書だけ、画面で選んだ空港名・車種を送る。完了した報告書は変更不可のため null
    const buildVehicleSelectionParams = (): HokkaidoAirportReportPostWorkInspectionSkipParams => {
      if (isSelectionLocked.value) return { airport_name: null, car_type: null };

      return { airport_name: state.selectedAirport, car_type: state.selectedCarType };
    };

    const completePostWorkInspection = async(): Promise<void> => {
      if (!canComplete.value) return;

      const reportId = props.hokkaidoAirportCar.report.id;
      const params: HokkaidoAirportReportPostWorkInspectionParams = {
        ...buildVehicleSelectionParams(),
        inspection_results: state.inspectionResults,
      };
      await submitInspection(async() => {
        const report = await hokkaidoAirportReportApi.completePostWorkInspection(reportId, params);
        emit('finishPostWorkInspection', report);
      }, '終了時点検の保存に失敗しました。再度操作を行ってください。');
    };

    const skipPostWorkInspection = async(): Promise<void> => {
      const reportId = props.hokkaidoAirportCar.report.id;
      await submitInspection(async() => {
        const report = await hokkaidoAirportReportApi.skipPostWorkInspection(reportId, buildVehicleSelectionParams());
        emit('finishPostWorkInspection', report);
      }, '終了時点検のスキップに失敗しました。再度操作を行ってください。');
    };

    return {
      state,
      confirmModalState,
      isRequesting,
      spLabels,
      airports,
      carTypes,
      isSelectionLocked,
      hasVehicleSelection,
      canComplete,
      completePostWorkInspection,
      skipPostWorkInspection,
    };
  },
});
